import React from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import moment from 'moment-mini';
import { Card, CardActionArea, CircularProgress, Typography, Tooltip, Chip, Box } from '@mui/material';
import {
  fetchLinkedConnectors,
  fetchLinkedConnector,
  fetchLinkedFlows,
  fetchSupportedFlows,
  fetchJob,
} from '../../redux/actions';
import { getSelectedFlow, isJobPending } from '../../redux/selectors';
import * as UIAction from '../../redux/actions/ui.actions';
import * as Utils from './ConnectorCardUtil';
import { getLogoUrl } from '@hotglue/common';
import  { WorkOutline as WorkOutlineIcon, WarningAmberOutlined as WarningAmberOutlinedIcon } from '@mui/icons-material';

const LinkedConnectorV2Card = ({ connector, flowId, tenantId }) => {
  const lastJob = useSelector((state) => state.jobs.lastJob[`${tenantId}/${flowId}/${connector.id}`]);
  const pendingJob = useSelector((state) => isJobPending(state, lastJob?.s3_root, flowId, tenantId));
  const hideJobStatusBadge = useSelector((state) => state.configuration.hideJobStatusBadge);
  const dispatch = useDispatch();
  React.useEffect(() => {
    if (!lastJob || lastJob.flow_id !== flowId) {
      // If there is no last job, check if there is one
      dispatch(fetchJob(undefined, undefined, connector.id, 'connector'));
    }
  }, [lastJob]);

  React.useEffect(() => {
    if (
      !pendingJob &&
      lastJob &&
      lastJob.status &&
      !(lastJob.status.includes('FAILED') || lastJob.status.includes('COMPLETE'))
    ) {
      console.log(`Polling job ${lastJob.job_id}`);
      Utils.pollJob(lastJob.s3_root, flowId, tenantId, connector.id);
    }
  }, [lastJob]);

  const { label, version, id } = connector;
  const icon = getLogoUrl(connector);
  const isFile = id === 'file';

  // Actions
  const onClickBody = () => {
    UIAction.goToEntityConfig(connector, 'connector');
  };

  return (
    <Card
      elevation={0}
      sx={{
        height: '160px',
        cursor: isFile ? 'pointer' : '',
        border: ({ palette }) => `1px solid ${palette.common.outline}`,
      }}
    >
      <CardActionArea
        id="LinkedConnectorCard"
        onClick={onClickBody}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          height: '100%',
          position: 'relative',
        }}
      >
        <Box top={0} right={0} left={0} position="absolute" height={'32px'}>
          <Box
            height={'32px'}
            width="100%"
            sx={{ position: 'relative', display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-end' }}
          >
            {pendingJob && !hideJobStatusBadge ? (
              <Tooltip title={`Job Running`} placement="top">
                <Box
                  position="relative"
                  display="flex"
                  role='tooltip'
                  aria-label='job running tooltip'
                  justifyContent="center"
                  alignItems="center"
                  height="28px"
                  width="28px"
                  mt={1}
                  mr={1}
                >
                  <WorkOutlineIcon sx={{ fontSize: '13px', position: 'absolute' }} />

                  <CircularProgress
                    size={28}
                    sx={{
                      color: '#FA5240',
                      position: 'absolute',
                      zIndex: 1,
                    }}
                  />
                </Box>
              </Tooltip>
            ) : lastJob && !hideJobStatusBadge ? (
              lastJob.status === 'JOB_COMPLETED' ? (
                <Tooltip
                  title={`Last synced at ${moment(lastJob.start_time).format('HH:mm MM/DD/YY')}`}
                  placement="top"
                >
                  <Box
                    border="1px solid #36C53C"
                    width={28}
                    role='tooltip'
                    aria-label={`Last synced at ${moment(lastJob.start_time).format('HH:mm MM/DD/YY')}`}
                    height={28}
                    borderRadius="32px"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    mt={1}
                    mr={1}
                  >
                    <WorkOutlineIcon sx={{ fontSize: '13px' }} />
                  </Box>
                </Tooltip>
              ) : (
                <Tooltip
                  title={`Sync failed at ${moment(lastJob.start_time).format('HH:mm MM/DD/YY')}`}
                  placement="top"
                >
                  <Box
                    border="1px solid #FA2A52"
                    width={28}
                    height={28}
                    role='tooltip'
                    aria-label={`Sync failed at ${moment(lastJob.start_time).format('HH:mm MM/DD/YY')}`}
                    borderRadius="32px"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    mt={1}
                    mr={1}
                  >
                    <WarningAmberOutlinedIcon sx={{ fontSize: '13px', color: '#FA2A52' }} />
                  </Box>
                </Tooltip>
              )
            ) : (
              <Box display="flex" role='tooltip' aria-label='linked tooltip' bgcolor="success.light" borderRadius="10px" mr={1}>
                <Typography variant="body2" fontSize="8px" color="success.dark" sx={{ px: 1, py: '2px' }}>
                  LINKED
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        <Box display="flex" flexDirection="column" gap={2} mt={1} alignItems="center">
          <Box component="img" role={'img'} alt={`image for ${label} connector`} width="48px" height="48px" src={icon} sx={{ objectFit: 'contain' }} />
          <Typography variant="h2" textAlign='center'>{label}</Typography>
        </Box>
        {version && (
        <Box
          sx={{
            position: 'absolute',
            bottom: 10,
            right: 10,
          }}
        >
          <Chip
            role='tooltip'
            aria-label='connector version'
            label={version}
            size="small"
            sx={{ color: '#676767', fontSize: '10px', height: '18px', fontWeight: '300' }}
          />
        </Box>
        )}
      </CardActionArea>
    </Card>
  );
};

const mapStateToProps = (state, props) => {
  const flowId = getSelectedFlow();

  return {
    lastJob: state.jobs.lastJob[flowId],
    flowId,
    tenantId: state.configuration.tenant_id,
    multipleTenantLinks: state.preferences.multipleTenantLinks,
    demo: state.configuration.demo || state.preferences.demoMode,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    fetchLinkedConnector: (connectorId) => dispatch(fetchLinkedConnector(connectorId)),
    fetchLinkedConnectors: () => dispatch(fetchLinkedConnectors()),
    fetchLinkedFlows: () => dispatch(fetchLinkedFlows()),
    fetchSupportedFlows: () => dispatch(fetchSupportedFlows()),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(LinkedConnectorV2Card);
